import { computed } from 'vue';
import {
  useGlobalDependencyProvider,
  type Permission,
} from '@teachino/shared-util';
import { useLicense } from './useLicense';
import {
  canUsePaywalledFeature,
  getUpsellBannerVisibility,
} from './soft-paywall';

/**
 * Whether the user can use a paywalled feature: paid license permission, or grace
 * / trial soft-paywall access for TE-2506 gated features (student sharing, IPI).
 */
export function useCanUsePaywalledFeature(feature: Permission) {
  const user = useGlobalDependencyProvider().inject('user');
  const { paywallLicenses } = useLicense();

  return computed(() =>
    canUsePaywalledFeature(
      feature,
      user.hasPermission(feature).value,
      paywallLicenses.value,
      user.accountCreatedAt.value,
    ),
  );
}

/** Upsell banner visibility and days-left messaging for a paywalled feature. */
export function useUpsellBannerVisibility(feature: Permission) {
  const user = useGlobalDependencyProvider().inject('user');
  const { paywallLicenses } = useLicense();

  return computed(() =>
    getUpsellBannerVisibility(
      feature,
      user.hasPermission(feature).value,
      paywallLicenses.value,
      user.accountCreatedAt.value,
    ),
  );
}

/**
 * Shows upsell banner during soft paywall (days left) or hard paywall (no access).
 */
export function useShowSoftPaywallUpsellBanner(feature: Permission) {
  const upsellBannerVisibility = useUpsellBannerVisibility(feature);

  return computed(() => upsellBannerVisibility.value.show);
}
